import React, { PropsWithChildren, useEffect, useRef } from "react"
import { Platform } from "react-native"

import { useAppState } from "@react-native-community/hooks"
import { useQueryClient } from "@tanstack/react-query"
import { usePathname, useRouter } from "expo-router"
import { usePostHog } from "posthog-react-native"
import { useMMKVString } from "react-native-mmkv"
import Toast from "react-native-toast-message"
import { WebSocketLike } from "react-use-websocket/dist/lib/types"
import { AvatarIdT, SidekickNameT } from "types/avatar"
import { TAddSentiment } from "types/sentiment"
import { TSentence, TViseme, TWord } from "types/speechMarks"
import { EskTypeT, SponsorT } from "types/sponsor"
import { TConversation, TMessage } from "types/transcript"
import {
  WebsocketAudioStreamingEndMessageData,
  WebsocketAudioStreamingStartMessageData,
  WebSocketMessageType,
} from "types/websocket"

import { DefaultEskType } from "src/app/constants"
import { useTyping } from "src/context/TypingContext"
import { useWebsocket } from "src/context/WebsocketContext"
import { useChatMessageEvents } from "src/hooks/useChatEvents"
import { SAMPLE_RATE_BASE } from "src/hooks/useMicrophoneStream"
import { useStores } from "src/models"
import { useCurrentMsk, useCurrentTTS } from "src/queries/useConfig"
import {
  addRecentlyClosedConversation,
  getCurrentConversation,
  getPastConversations,
  getSynchronizeAllConversationsFromQueryStatus,
  setPastConversations,
  synchronizeAllConversationsFromQuery,
  updateConversationFromStartMessage,
  useCurrentConversation,
  useStartConversationStatus,
} from "src/queries/useConversation"
import { useLLMAvailable } from "src/queries/useLLMAvailable"
import { useMember } from "src/queries/useUser"
import {
  getSafeWebsocketErrorInfo,
  logSentryBreadcrumb,
  logSentryEvent,
} from "src/utils/crashReporting"
import { getTempConversation } from "src/utils/getTempConversation"
import { computeMessageKey } from "src/utils/messageUtils"
import { sessionScopedStorage } from "src/utils/storage/mmkv"
import { getTimezoneNameIANA, nowInUTC } from "src/utils/time"
import { WebSocketMessage } from "src/utils/WebSocketMessage"
import { useAuth } from "./AuthContext"
import { useLocation } from "./LocationContext"
import { useMediaContext } from "./MediaContextProvider"
import { useResponseTiming } from "./ResponseTimingContext"
import { useRole } from "./RoleContext"
import { useSentimentModal } from "./SentimentModalContext"
import { useVisemes } from "./VisemeContext"

const getLatestMessage = (conversation?: TConversation | null) => {
  return conversation?.transcript.slice(-1)[0]
}

// This AudioChunkT is used to store the audio file path and the speech marks associated with the audio.
export interface AudioChunkT {
  id: string
  speechMarks: Array<TSentence | TWord | TViseme>
  base64?: string
  /** Remote audio URL — used instead of base64 when audio is served from a CDN (e.g. unauth intro message). */
  audioUrl?: string
  processed?: boolean
}

export interface StartConversationMessage {
  is_sentence_mode?: boolean
  llm_id?: string
  debug_mode?: string
  alternate_llm_id?: string
  type: WebSocketMessageType.StartConversation
  sttId?: string
  isAudioOn?: boolean
  isDebugOn?: boolean
  active_task_id?: string | null
  active_esk_sponsor?: string
  active_esk_type?: string | number
  sub_conversation?: boolean
  timezone?: string
  location?: { latitude: number; longitude: number } | null
  suppress_first_message?: boolean
}

export type ChatContextType = {
  sendMessage: (msg: string) => void

  startConversation: (
    activeESKSponsor: SponsorT,
    activeESKType: EskTypeT,
    sidekickAvatarId: AvatarIdT,
    sidekickName: SidekickNameT,
    taskId?: string | null,
    completelyReset?: boolean,
    llmId?: string,
    suppressFirstMessage?: boolean,
  ) => Promise<void>
  startNewConversation: (
    taskId?: string | null,
    llmId?: string,
    suppressFirstMessage?: boolean,
  ) => void

  currentLLMId: string | undefined
  setCurrentLLMId: (llmId: string | undefined) => void
  showDebugMessages: boolean
  setShowDebugMessages: (arg0: boolean) => void

  audioStreamingReady: boolean
  startMicStream: () => void
  sendAudioChunk: (chunk: Buffer) => void
  stopMicStream: () => void
  cancelMicStream: () => void
  cancelWaitingForWelcomeMessage: () => void

  sendSimulateEvent: (eventType: string) => void
  sendUserReport: (message: string) => void

  isSwitchingSidekick: boolean
  setIsSwitchingSidekick: (arg0: boolean) => void

  startConversationStatus: { isPending: boolean }
  startConversationTimedOut: boolean
  clearStartConversationTimedOut: () => void

  wordsDone: boolean

  waitingForWelcomeMessage: boolean

  isSidekickBusy: boolean
}

// Indicates that we want to receive audio from the backend in the websocket connection.
const AUDIO_ON = true

export const ChatContext = React.createContext<ChatContextType | null>(null)

/**
 * ChatProvider:
 *
 * returns:
 * - sendMessage: function to send a message to the websocket
 * - startConversation: function to start a new conversation
 * - startNewConversation: function to start a new conversation with a new ESK
 * - showDebugMessages: a boolean indicating if debug messages should be shown
 * - setShowDebugMessages: function to toggle the debug messages
 * - startMicStream: function to start streaming audio
 * - sendAudioChunk: function to send an audio chunk to the websocket
 * - stopMicStream: function to stop streaming audio
 * - sendSimulateEvent: function to send a simulate event message to the websocket
 */
const MEDIA_PLAYER_ROUTES = new Set([
  "/video-player",
  "/audio-player",
  "/youtube-player",
  "/file-viewer",
  "/pdf-viewer",
  "/image-viewer",
  "/web-browser",
])

export const ChatProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const router = useRouter()
  const pathname = usePathname()
  const posthog = usePostHog()
  const {
    setAwaitingUserSentence,
    setAwaitingESKResponse,
    awaitingESKResponse,
    awaitingUserSentence,
  } = useTyping()
  const {
    typingStore: {
      resetString: resetTypingString,
      getMessageValue: getTypingMessageValue,
      appendString: appendTypingString,
      incomingMessage,
    },
  } = useStores()
  const { getAuthToken, needsRefresh, sponsorId } = useAuth()
  const { queueSpeech, stopSpeech, signalAllAudioReceived, isSpeaking, waitForSpeechQueueEmpty } =
    useVisemes()
  const { setCurrentLLMId: setResponseTimingLLMId, cancelTimingOnError } = useResponseTiming()
  const { verifyAndRestoreSession } = useRole()
  const currentTTSEngine = useCurrentTTS()
  const currentMsk = useCurrentMsk()
  const member = useMember()
  const llmAvailable = useLLMAvailable()
  const { getMediaContextInfo } = useMediaContext()
  const { refreshLocation } = useLocation()

  if (!currentMsk) {
    console.error("[CHAT_CONTEXT] Critical error: Current MSK not found in ChatProvider.")
  }
  const mskName = currentMsk?.name

  const { sentimentText, pendingSentiment, commitSentiment } = useSentimentModal()

  // Stores audio chunks that are received from the websocket connection
  const audioChunk = React.useRef<AudioChunkT | undefined>(undefined)

  // This flag is used to determine if the sidekick is done sending words to the frontend.
  // We flip it to true when each done message is received from the websocket
  // Which is then used to determine if we can execute the message events.
  // Once we execute the message events, we flip these back to false and assume that we're now waiting
  // for the next set of messages.
  const wordsDone = React.useRef<boolean>(true)

  const [showDebugMessages, setShowDebugMessages] = React.useState(false)
  const [audioStreamingReady, setAudioStreamingReady] = React.useState<boolean>(false)
  const [messageEvents, setMessageEvents] = React.useState<TMessage[]>([])
  // Per-tab so a debug LLM override doesn't leak between tabs that may be
  // operating against different sponsors/roles.
  const [currentLLMId, setCurrentLLMId] = useMMKVString("chat.currentLLMId", sessionScopedStorage)
  const [waitingForWelcomeMessage, setWaitingForWelcomeMessage] = React.useState<boolean>(false)
  const welcomeMessageTimeoutRef = useRef<number | null>(null)
  const typingTimerRef = useRef<NodeJS.Timeout | null>(null)

  const { data: currentConversation, setCurrentConversation } = useCurrentConversation()
  const resetThinkingIndicatorsTimeout = useRef<number | null>(null)
  const { data: startConversationStatus } = useStartConversationStatus()
  const { api } = useAuth()
  const queryClient = useQueryClient()
  const [isSwitchingSidekick, setIsSwitchingSidekick] = React.useState(false)
  const [startConversationTimedOut, setStartConversationTimedOut] = React.useState(false)
  const startConversationWatchdogRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  const START_CONVERSATION_TIMEOUT_MS = 30000

  // Watchdog: if startConversationStatus stays pending for too long without a server response,
  // unblock the UI and let the user retry or log out.
  React.useEffect(() => {
    if (startConversationStatus?.isPending) {
      startConversationWatchdogRef.current = setTimeout(() => {
        console.warn(
          `[CHAT_CONTEXT] startConversation timed out after ${START_CONVERSATION_TIMEOUT_MS}ms with no server response.`,
        )
        queryClient.setQueryData(["startConversationStatus"], { isPending: false })
        setStartConversationTimedOut(true)
        startConversationWatchdogRef.current = null
      }, START_CONVERSATION_TIMEOUT_MS)
    } else {
      if (startConversationWatchdogRef.current) {
        clearTimeout(startConversationWatchdogRef.current)
        startConversationWatchdogRef.current = null
      }
    }
    return () => {
      if (startConversationWatchdogRef.current) {
        clearTimeout(startConversationWatchdogRef.current)
        startConversationWatchdogRef.current = null
      }
    }
  }, [startConversationStatus?.isPending, queryClient])

  const clearStartConversationTimedOut = React.useCallback(() => {
    setStartConversationTimedOut(false)
  }, [])

  // On reload (ChatProvider mount), refetch conversations/current so server is source of truth.
  // Foreground is handled by SidekickChat: AppState "active" -> trySynchronizeChatHistory -> sync (which fetchQueries current).
  // Defer and only run when not synchronizing so we don't cancel sync's fetchQuery (CancelledError).
  React.useEffect(() => {
    const id = setTimeout(() => {
      if (!getSynchronizeAllConversationsFromQueryStatus()) {
        queryClient.invalidateQueries({ queryKey: ["conversations", "current"] })
      }
    }, 0)
    return () => clearTimeout(id)
  }, [queryClient])

  // Busy queue: holds websocket messages received during a sidekick switch while the current sidekick is still busy
  const busyQueueRef = React.useRef<MessageEvent[]>([])
  const [isSidekickBusy, setIsSidekickBusy] = React.useState(false)

  // Helper to check if the sidekick is currently busy (speaking or text animating)
  // - isSpeaking: audio is currently playing
  // - incomingMessage.length > 0: words are still being streamed/animated
  const getIsSidekickCurrentlyBusy = React.useCallback(() => {
    return isSpeaking || incomingMessage.length > 0
  }, [isSpeaking, incomingMessage])

  // Constants
  const WELCOME_MESSAGE_TIMEOUT = 30 * 1000

  const cleanupWelcomeMessageTimeout = React.useCallback(() => {
    setWaitingForWelcomeMessage(false)
    if (welcomeMessageTimeoutRef.current) {
      clearTimeout(welcomeMessageTimeoutRef.current)
      welcomeMessageTimeoutRef.current = null
    }
  }, [])

  const cleanupTypingTimerTimeout = React.useCallback(() => {
    if (typingTimerRef.current) {
      clearTimeout(typingTimerRef.current)
      typingTimerRef.current = null
    }
  }, [])

  // Helper function to resolve/cancel waiting for welcome message
  const resolveWaitingForWelcomeMessage = React.useCallback(() => {
    console.log("[CHAT_CONTEXT] Welcome message received")
    cleanupWelcomeMessageTimeout()
  }, [cleanupWelcomeMessageTimeout])

  // Helper function to cancel waiting for welcome message
  const cancelWaitingForWelcomeMessage = React.useCallback(() => {
    console.log("[CHAT_CONTEXT] Cancelling waiting for welcome message")
    cleanupWelcomeMessageTimeout()
  }, [cleanupWelcomeMessageTimeout])

  // Set up timeout when waiting for welcome message
  const startWaitingForWelcomeMessage = React.useCallback(() => {
    console.log("[CHAT_CONTEXT] Start waiting for welcome message, blocking microphone input")

    // Always clear any existing timer first — the cleanup helper is idempotent and
    // checking the captured `waitingForWelcomeMessage` value here would miss a stale
    // timer when consecutive start_conversation events fire in the same handler tick.
    if (welcomeMessageTimeoutRef.current) {
      clearTimeout(welcomeMessageTimeoutRef.current)
      welcomeMessageTimeoutRef.current = null
    }

    setWaitingForWelcomeMessage(true)

    // Set a timeout to unblock microphone if no message is received
    welcomeMessageTimeoutRef.current = setTimeout(() => {
      console.log("[CHAT_CONTEXT] Welcome message timeout reached")
      cancelWaitingForWelcomeMessage()
    }, WELCOME_MESSAGE_TIMEOUT)
  }, [WELCOME_MESSAGE_TIMEOUT, cancelWaitingForWelcomeMessage])

  // Clean up timers on unmount
  React.useEffect(() => {
    return () => {
      cleanupTypingTimerTimeout()
      cleanupWelcomeMessageTimeout()
    }
  }, [cleanupTypingTimerTimeout, cleanupWelcomeMessageTimeout])

  // Clear busy queue when conversation is lost (conversation is lost or cleared)
  React.useEffect(() => {
    if (!currentConversation) {
      if (busyQueueRef.current.length > 0 || isSidekickBusy) {
        console.log("[CHAT_CONTEXT] Conversation lost, clearing busy queue")
        busyQueueRef.current = []
        setIsSidekickBusy(false)
      }
    }
  }, [currentConversation, isSidekickBusy])

  const appendMessageToHistory = React.useCallback(
    async (message: TMessage) => {
      console.log("[CHAT_CONTEXT] Appending message to query data", message)

      // Read directly from MMKV (synchronous) rather than from the React-state closure.
      // The React state can lag by one render cycle after setCurrentConversation() is called,
      // so using the stale closure value here risks appending to an already-archived conversation,
      // which would create duplicate messages between current and past.
      let conversation = getCurrentConversation() ?? null
      if (!conversation) {
        console.log(
          "[CHAT_CONTEXT] No existing data in cache for current conversation. Creating a new one.",
        )

        if (!member || !sponsorId || !currentMsk) {
          console.error(
            "[CHAT_CONTEXT] Cannot create temp conversation: missing member, activeSponsor, or currentMsk data.",
            "\ncurrentMsk:",
            currentMsk,
            "\nmember:",
            member,
          )
          return
        }

        // Mock a conversation as a placeholder for the current conversation.
        // This may be necessary when a new conversation starts and the websocket sends a message before the query is refetched
        // FIXME: when creating a temp conversation, we don't know if we're starting it with an ESK or MSK,
        // so we mock the conversation as occurring with the MSK, the closest thing we have to a good guess
        // On top of this, the default MSK has special case fields for EskType and EskSponsor
        const activeEskSponsor = sponsorId
        const activeEskType =
          currentMsk.id === DefaultEskType ? DefaultEskType : parseInt(currentMsk.id)
        const sidekickAvatarId = parseInt(currentMsk.avatar_id)
        const sidekickName = currentMsk.name

        conversation = getTempConversation({
          sidekick_avatar_id: sidekickAvatarId,
          sidekick_name: sidekickName,
          active_esk_sponsor: activeEskSponsor,
          active_esk_type: activeEskType,
          transcript: [],
        })
      }

      // Idempotent append: avoid duplicates after long sleep/reconnect (QA: multiple messages after phone asleep for hours).
      // 1) If a message with the same message_id+role already exists, skip.
      const newKey = computeMessageKey(message)
      if (newKey) {
        const existingKeys = new Set(
          (conversation.transcript || []).map((m) => computeMessageKey(m)).filter(Boolean),
        )
        if (existingKeys.has(newKey)) {
          console.log("[CHAT_CONTEXT] Skipping duplicate message (already in transcript)", newKey)
          return conversation
        }
      }
      // 2) User messages only: re-delivery (e.g. sentence event replayed after reconnect) often uses client-generated
      // ids (sentence-${Date.now()}) so message_id differs each time. Skip if the last message is already a member
      // message with the same text (catches re-append without blocking user from saying the same thing again later).
      if (message.role === "member" && (message.text?.trim() ?? "") !== "") {
        const transcript = conversation.transcript || []
        const last = transcript[transcript.length - 1]
        if (last?.role === "member" && (last.text?.trim() ?? "") === (message.text?.trim() ?? "")) {
          if (__DEV__) {
            console.log(
              "[CHAT_CONTEXT] Skipping duplicate member message (last message already has same text)",
            )
          }
          return conversation
        }
      }
      // When a sidekick response arrives with a server-provided timestamp, ensure that any
      // preceding optimistic member messages (whose timestamps were set from the client clock via
      // nowInUTC()) sort before it. If the client clock is ahead of the server clock, a member
      // message stamped at T_client can be numerically greater than the sidekick response stamped
      // at T_server, making the user's question appear visually *after* the answer.
      //
      // This is especially visible in suppress_first_message flows (e.g. tapping a video in the
      // Content Library). The backend sends a PlayVideo event but does NOT add that message to the
      // conversation history (backend bug C2-2210), so the REST transcript is shorter than the
      // local MMKV transcript. The MMKV-wins merge condition (REST.length < MMKV.length) is
      // therefore always true, and the server's corrected user-message timestamp never gets
      // applied via the REST sync path.
      //
      // Fix: clamp any member message whose created_at >= the incoming server response's
      // created_at to (server_response_created_at - 1 ms) so the display order is always:
      //   question → answer.
      //
      // This is safe when C2-2210 is resolved: once REST.length matches MMKV.length, REST wins
      // the merge and replaces all timestamps with the correct server values anyway — this
      // client-side correction becomes a no-op.
      if (message.role !== "member" && message.created_at) {
        const serverResponseMs = new Date(message.created_at).getTime()
        if (!isNaN(serverResponseMs)) {
          let correctionNeeded = false
          const fixedTranscript = conversation.transcript.map((msg) => {
            if (msg.role === "member" && msg.created_at) {
              const memberMs = new Date(msg.created_at).getTime()
              if (!isNaN(memberMs) && memberMs >= serverResponseMs) {
                correctionNeeded = true
                return { ...msg, created_at: new Date(serverResponseMs - 1).toISOString() }
              }
            }
            return msg
          })
          if (correctionNeeded) {
            console.log(
              "[CHAT_CONTEXT] Correcting member message timestamp(s) ahead of server response — client clock skew detected",
            )
            conversation.transcript = fixedTranscript
          }
        }
      }

      conversation.transcript.push(message)
      setCurrentConversation({ ...conversation })

      return conversation
    },
    [setCurrentConversation, member, sponsorId, currentMsk],
  )

  const streamingReady = React.useCallback(
    async (streamingReady: boolean) => {
      await stopSpeech() // Stop any speech that may be playing because we're trying to talk now.
      setAudioStreamingReady(streamingReady)
      if (streamingReady && resetThinkingIndicatorsTimeout.current) {
        clearTimeout(resetThinkingIndicatorsTimeout.current)
      }
    },
    [stopSpeech],
  )

  React.useEffect(() => {
    if (awaitingESKResponse && !awaitingUserSentence) {
      // account for the case where the user is done speaking, but no text is recognized
      const lastMessage = getLatestMessage(currentConversation)
      if (lastMessage && lastMessage.role === "member") {
        wordsDone.current = false
      }
    }
  }, [awaitingESKResponse, awaitingUserSentence, currentConversation])

  const {
    sendJsonMessage: wsSendMessage,
    sendMessage: wsSendBinaryMessage,
    isConnected,
    getIsConnected,
    forceReconnect,
    getWebSocket,
  } = useWebsocket()

  // If the refresh token is about to expire or the websocket is disconnected, we need to queue messages.
  // Messages are stored in a queue and sent after the token has been refreshed or the websocket reconnects.
  // This is done to mitigate the authed URL for the websocket connection changing mid conversation,
  // and to prevent message loss when the websocket is temporarily disconnected.
  const [reconnectionMessages, setReconnectionMessages] = React.useState<
    { socketMessage: any; messageData: TMessage | undefined }[]
  >([])
  const sendMessageWithTokenVerification = React.useCallback(
    (socketMessage: any, messageData?: TMessage) => {
      if (needsRefresh()) {
        console.log("[CHAT_CONTEXT] Needs auth token refresh, queueing message")
        setReconnectionMessages((prev) => [...prev, { socketMessage, messageData }])
        getAuthToken()
      } else if (!getIsConnected()) {
        console.log("[CHAT_CONTEXT] Websocket not connected, queueing message")
        setReconnectionMessages((prev) => [...prev, { socketMessage, messageData }])
      } else {
        console.log("[CHAT_CONTEXT] Auth token is valid and websocket connected")
        posthog?.capture("send_message", {
          taskId: currentConversation?.active_task_id ?? null,
          activeESKSponsor: currentConversation?.active_esk_sponsor ?? "unknown-sponsor",
          role: messageData?.role ?? "unknown-role",
          conversationId: currentConversation?.id ?? "unknown-conversation-id",
          skPlatform: "app",
        })
        wsSendMessage(socketMessage)
        if (messageData) {
          appendMessageToHistory(messageData)
        }
      }
    },
    [
      needsRefresh,
      getAuthToken,
      getIsConnected,
      posthog,
      currentConversation?.id,
      currentConversation?.active_task_id,
      currentConversation?.active_esk_sponsor,
      wsSendMessage,
      appendMessageToHistory,
    ],
  )

  const startConversation = React.useCallback(
    async (
      activeESKSponsor: SponsorT,
      activeESKType: EskTypeT,
      sidekickAvatarId: AvatarIdT,
      sidekickName: SidekickNameT,
      taskId: string | null = null,
      completelyReset = false,
      llmId?: string,
      suppressFirstMessage?: boolean,
    ) => {
      if (startConversationStatus?.isPending) {
        console.error(
          "[CHAT_CONTEXT] Start Conversation already in progress. Skipping startConversation request.",
        )
        return
      }

      // Clear any previous rejection so the new attempt is unblocked
      queryClient.setQueryData(["conversationRejectedStatus"], { isRejected: false })

      // Set the pending status to true when starting a conversation
      queryClient.setQueryData(["startConversationStatus"], { isPending: true })

      // Force a websocket reconnection, which also closes the current conversation
      // based on reverse-engineering what happens on RLIT. `start_conversation` is expected
      // to be the first message sent on the new websocket.
      forceReconnect()

      // TODO: Verify these properties. some of them seem redundant and what's with snake and camel case?
      // Check with the documentation from Gil & Ran

      const startLocation = await refreshLocation()
      console.log("[CHAT_CONTEXT] startConversation location:", startLocation)
      const messageData: StartConversationMessage = {
        active_esk_sponsor: activeESKSponsor,
        debug_mode: "false",
        isAudioOn: true,
        isDebugOn: false,
        active_task_id: taskId ?? null,
        type: WebSocketMessageType.StartConversation,
        sub_conversation: !completelyReset,
        sttId: "aws_transcribe",
        is_sentence_mode: true,
        timezone: getTimezoneNameIANA(),
        location: startLocation ?? null,
      }

      // Only send llm_id if explicitly set by the user (not undefined)
      // Backend uses its default LLM when llm_id is omitted
      if (llmId || currentLLMId) {
        messageData.llm_id = llmId || currentLLMId
      }

      if (suppressFirstMessage) {
        messageData.suppress_first_message = true
      }

      // Add the esk type if we're not using the MSK
      if (sidekickName !== mskName) {
        messageData.active_esk_type = activeESKType
      }

      posthog?.capture("start_conversation", {
        taskId,
        activeESKSponsor,
        activeESKType,
        sidekickAvatarId,
        sidekickName,
        skPlatform: "app",
      })

      // Archive the old conversation and replace current — all synchronous MMKV writes in one
      // call stack. React batches them into a single render so no render ever sees the same
      // conversation in both current and past simultaneously (which causes duplicate-message
      // errors in useChatMessageList).
      const convBeforeSwitch = getCurrentConversation()
      const localPast = getPastConversations()
      if (convBeforeSwitch && !localPast.some((c) => c?.id === convBeforeSwitch.id)) {
        addRecentlyClosedConversation(convBeforeSwitch)
        setPastConversations([convBeforeSwitch, ...localPast])
      }
      const tempConversation = getTempConversation({
        active_esk_sponsor: activeESKSponsor,
        active_esk_type: activeESKType,
        sidekick_avatar_id: sidekickAvatarId,
        sidekick_name: sidekickName,
        transcript: [],
      })
      console.log(
        "[CHAT_CONTEXT] Replacing current conversation with new temp conversation",
        tempConversation,
      )
      setCurrentConversation(tempConversation)

      try {
        sendMessageWithTokenVerification(messageData)
      } catch (error) {
        console.error("[CHAT_CONTEXT] Error sending start conversation message:", error)
        queryClient.setQueryData(["startConversationStatus"], { isPending: false })
      }
    },
    [
      startConversationStatus?.isPending,
      queryClient,
      forceReconnect,
      currentLLMId,
      refreshLocation,
      mskName,
      posthog,
      setCurrentConversation,
      sendMessageWithTokenVerification,
    ],
  )

  const startNewConversation = React.useCallback(
    (taskId: string | null = null, llmId?: string, suppressFirstMessage?: boolean) => {
      if (!member || !currentMsk || !sponsorId) {
        console.error(
          "[CHAT_CONTEXT] startNewConversation: Missing member, currentMsk, or sponsorId. Cannot start new conversation.",
        )
        Toast.show({
          type: "error",
          text1: "Cannot Start Conversation",
          text2: "User or Sidekick data is not available.",
        })
        return
      }

      const activeESKSponsor = sponsorId
      const activeESKType =
        currentMsk.id === DefaultEskType ? DefaultEskType : parseInt(currentMsk.id)
      const sidekickAvatarId = parseInt(currentMsk.avatar_id)
      const sidekickName = currentMsk.name

      startConversation(
        activeESKSponsor,
        activeESKType,
        sidekickAvatarId,
        sidekickName,
        taskId,
        true,
        llmId,
        suppressFirstMessage,
      )
    },
    [member, currentMsk, sponsorId, startConversation],
  )

  const handleMessage = React.useCallback(
    async (e: MessageEvent<any>) => {
      // Creates a new object to parse the incoming message
      const messageObj = new WebSocketMessage(e.data)

      // handle start conversation error {"error": "ERR-01: Cannot open conversation. A started conversation in progress.", "status_code": 409}
      // FIXME: parse and handle this message like other error type messages
      if (messageObj.status_code === 409) {
        if (currentConversation) {
          if (startConversationStatus?.isPending) {
            console.log(
              "[CHAT_CONTEXT] Received 409 error (conversation already in progress), but we have a current conversation, and startConversationStatus is pending.",
            )
            queryClient.setQueryData(["startConversationStatus"], { isPending: false })
          } else {
            console.log(
              "[CHAT_CONTEXT] Received 409 error (conversation already in progress), but we have a current conversation, but startConversationStatus is not pending.",
            )
          }
        } else {
          // Attempt to query the conversation we've been told already exists, and clean up start conversation status
          console.log(
            "[CHAT_CONTEXT] Received 409 error (conversation already in progress), but have no current conversation. Recovering current conversation.",
          )

          // We don't have a conversation, so we synchronize to recover the current one
          if (!getSynchronizeAllConversationsFromQueryStatus()) {
            await synchronizeAllConversationsFromQuery(api, queryClient)
          }

          // We know we have either synchronized, or were already synching when we were told a conversation is already started.
          // We clear this so a new conversation can be attempted later as needed.
          queryClient.setQueryData(["startConversationStatus"], { isPending: false })
        }

        return
      }

      // Error
      if (messageObj.isError()) {
        // Reset the state of the chat context
        resetTypingString()
        setAwaitingUserSentence(false)
        setAwaitingESKResponse(false)
        audioChunk.current = undefined
        setMessageEvents([])
        wordsDone.current = true

        // Ensure we reset start conversation status on error so UI doesn't get stuck
        queryClient.setQueryData(["startConversationStatus"], { isPending: false })
        setIsSwitchingSidekick(false)

        // Unblock microphone input on error.
        // Always call the cleanup helper (it's idempotent); guarding on the
        // captured `waitingForWelcomeMessage` value would miss the case where
        // the previous message in the same handler tick set the flag — the
        // closure here still sees the pre-update value.
        cancelWaitingForWelcomeMessage()

        // Cancel timing measurement since an error occurred that prevents response
        const errorCode =
          messageObj.parsedData && "code" in messageObj.parsedData
            ? messageObj.parsedData.code
            : "unknown-error-code"
        cancelTimingOnError(`Chat error: ${errorCode}`)

        // Check if we want to attempt recovery by starting a new conversation
        if (messageObj.isWebsocketError() && messageObj.parsedData?.code === "ERR-21") {
          // First verify the user's role is correct before starting a new conversation.
          // ERR-21 may occur when the role cookie expired, so we need to restore the role first.
          const wasRestored = await verifyAndRestoreSession(true)
          if (wasRestored) {
            const message =
              "[CHAT_CONTEXT]onMessage: Role was restored during ERR-21 recovery, waiting for session to stabilize before starting new conversation"
            console.warn(message)
            logSentryEvent("warning", message)
            // Brief delay to ensure backend session is fully established after REST calls
            await new Promise((resolve) => setTimeout(resolve, 500))
          } else {
            const message =
              "[CHAT_CONTEXT]onMessage: Failed to verify role during ERR-21 recovery, proceeding anyway"
            console.error(message)
            logSentryEvent("error", message)
          }
          startNewConversation()
          // FIXME: we also need to resend our last message to the SK or else mark it as failed, because this message indicates we had no conversation to send it to
          // and so it was dropped by the backend
        } else {
          console.error("[CHAT_CONTEXT]onMessage: Error message", messageObj)
        }

        const safeErrorInfo = getSafeWebsocketErrorInfo(messageObj)
        logSentryBreadcrumb(`[CHAT_CONTEXT]onMessage: ERROR: ${JSON.stringify(safeErrorInfo)}`)
        return
      }

      // Streaming readiness status:
      if (messageObj.isStreamingReadyEvent) {
        streamingReady(messageObj.isStreamingReady)
        return
      }

      if (messageObj.isUserTalkingEvent) {
        // this will be used more with "auto mode" for mic streaming
        if (messageObj.isUserTalking === false) {
          // if the user is done talking, we may want to remove the thinking indicator
          console.log("[CHAT_CONTEXT] User is done talking")

          // at this point we don't know if the backend will send us transcribed audio or not
          // so we'll wait a few seconds before removing the thinking indicator
          resetThinkingIndicatorsTimeout.current = setTimeout(() => {
            // Get the last message from the current conversation
            const lastMessage = getLatestMessage(currentConversation)

            // if the last message was from the member user, that means we got transcribed audio and no action is necessary
            // if the last message is still from ESK or system, that means we didn't get any user transcribed audio and we should remove the thinking indicators
            // the user likely said nothing.
            if (lastMessage && lastMessage.role !== "member") {
              setAwaitingUserSentence(false)
            }
            // This timeout controls how long we wait before removing the thinking indicators.
            // The dev server was returning very slowly so we were removing it too soon at 200ms.
            // This is mainly used for when the user taps the mic button and doesn't say anything
            // then taps off. We want to remove the thinking indicators after a few seconds while
            // still allowing enough time for the websocket message to come back to the user's device.
          }, 2000)
        }
        return
      }

      // Sentence buffer done - signal all audio received and continue with standard word done handling
      if (messageObj.isSentenceBufferDone()) {
        signalAllAudioReceived()
        // Fall through to handle as word done below
      }

      // Words are done streaming.
      if (messageObj.isWordDone() || messageObj.isSentenceBufferDone()) {
        wordsDone.current = true

        // If we receive a done message while still awaiting the start_conversation event (e.g.
        // the server responded with audio but never sent a start_conversation event, as happens
        // when a user's trial has ended), clear the pending status so the watchdog doesn't
        // fire the "Server not responding" alert.
        if (startConversationStatus?.isPending) {
          queryClient.setQueryData(["startConversationStatus"], { isPending: false })
        }

        // Unblock microphone input when we receive the welcome message from the server.
        // Always resolve unconditionally — the helper is idempotent, and gating on
        // the captured `waitingForWelcomeMessage` value drops the resolve when the
        // start_conversation event and the sentenceBuffer "done" arrive in the same
        // handler tick (the start_conversation handler sets the flag via setState,
        // but this closure still sees the pre-update value, so the typed indicator
        // would remain stuck until the welcome-message timeout fires).
        resolveWaitingForWelcomeMessage()

        // we're assuming that the word done message will always be sent to indicate the end of a message,
        // and audio / speech marks will follow
        // check for a speech mark id to determine if audio is expected. some messages (like the simulated events) don't have a speech mark id
        console.log("[CHAT_CONTEXT] Word done message", JSON.stringify(messageObj, null, 2))
        console.log("[CHAT_CONTEXT] Sending message buffer for ESK")

        if (messageObj.events.length > 0) {
          // Check for SwitchCM in events array and set switching flag immediately to block UI
          const hasSwitchCM = messageObj.events.some(
            (event: any) => event.event_type === "SwitchCM",
          )
          if (hasSwitchCM) {
            setIsSwitchingSidekick(true)
          }

          // Check for ERR-32 (trial ended) — server rejected the conversation.
          // Disable the Talk button so the user doesn't get stuck in a loop.
          const hasTrialEnded = messageObj.events.some(
            (event: any) => event.type === "error" && event.code === "ERR-32",
          )
          if (hasTrialEnded) {
            console.warn(
              "[CHAT_CONTEXT] ERR-32 received: trial ended. Blocking further Talk attempts.",
            )
            queryClient.setQueryData(["conversationRejectedStatus"], { isRejected: true })
          }

          console.log("[CHAT_CONTEXT] Adding message events", messageObj.events)
          setMessageEvents((prev) => [...prev, messageObj])
        }

        const newMessage: TMessage = {
          id: new Date().getTime(),
          text: getTypingMessageValue(),
          created_at: messageObj.data.created_at,
          role: messageObj.data.role,
          events: messageObj.data.events,
          intent: messageObj.data.intent,
          alternate_intent: messageObj.data.alternate_intent,
          prompt_id: messageObj.data.prompt_id,
          message_id: messageObj.data.message_id,
          speech_mark_id: messageObj.data.speech_mark_id,
          audio_id: messageObj.data.audio_id,
        }

        // FIXME: there is a backend bug where the very first message in a root conversation
        // includes no created_at timestamp or role
        if (!newMessage.created_at) {
          // It's enough to mark it with the app's current time to maintain the correct UI ordering
          newMessage.created_at = nowInUTC()
        }
        if (!newMessage.role) {
          // Because this bug affects the first message of a root conversation, we know it has to be the MSK
          newMessage.role = "msk"
        }

        appendMessageToHistory(newMessage)

        // Only clear awaitingESKResponse if no audio is expected
        // This prevents race condition between "word done" and audio arriving in old protocol
        const hasAudio = messageObj.data.speech_mark_id || messageObj.data.audio_id
        if (!hasAudio) {
          // No audio for this message (e.g., system messages, simulated events)
          setAwaitingESKResponse(false)
        }
        // If audio is expected, awaitingESKResponse stays true until audio plays (isSpeaking becomes true)
        // This is handled by: (awaitingESKResponse && !isSpeaking) in SidekickInteractionContext

        resetTypingString()
        return
      }

      // Actual word messages:
      if (messageObj.isWord()) {
        // Otherwise:
        // Only add non-empty words from the main sidekick
        if (messageObj.word && messageObj.word.length > 0) {
          appendTypingString(messageObj.word)
        }
        return
      }

      // Sentence Messages: These are messages that contain the transcribed text that the user has spoken
      // FIXME: create a sentence type and type guard
      if (messageObj.isSentence) {
        if (messageObj.isCancelled) {
          console.log("[CHAT_CONTEXT] Streaming cancelled by user")
          setAwaitingUserSentence(false)
          setAwaitingESKResponse(false)
          wordsDone.current = true
          return
        }
        if (messageObj.sentence?.length === 0) {
          console.log("[CHAT_CONTEXT] Empty sentence message", messageObj)
          posthog?.capture("speech_transcription_empty", {
            skPlatform: "app",
          })
          setAwaitingUserSentence(false)
          setAwaitingESKResponse(false)
          wordsDone.current = true
          return
        }
        if (messageObj.isSentenceDone) {
          // data: {status: "done"} object is sent when the user is done speaking in auto-mode. We can ignore this for now, as we're not using auto-mode.
          console.log("[CHAT_CONTEXT] Sentence done message", messageObj)
          if (messageObj.events.length > 0) {
            setMessageEvents((prev) => [...prev, messageObj])
          }
          return
        }
        // 'sentence' is used to return the user's message after audio has been transcribed by backend
        console.log("[CHAT_CONTEXT] Sentence message", messageObj)
        posthog?.capture("speech_transcription_complete", {
          sentence_length: messageObj.sentence?.length || 0,
          skPlatform: "app",
        })
        const messageId = new Date().getTime()
        const newMessage: TMessage = {
          id: messageId,
          message_id: `sentence-${messageId}`,
          text: messageObj.sentence,
          created_at: nowInUTC(),
          role: messageObj.role,
        }
        console.log("[CHAT_CONTEXT] Appending message to query data", newMessage)
        appendMessageToHistory(newMessage)

        // Don't show the thinking indicator if the user is done speaking
        setAwaitingUserSentence(false)
        setAwaitingESKResponse(true)
        return
      }

      if (messageObj.isSentenceBuffer && messageObj.sentenceBuffer) {
        const { audio, sentence, speech_marks: speechMarks } = messageObj.sentenceBuffer
        if (!sentence || !sentence.data) {
          console.error("[CHAT_CONTEXT] sentenceBuffer sentence is empty", messageObj)
          return
        }
        console.log("[CHAT_CONTEXT] Sentence buffer message", sentence.data)
        appendTypingString(sentence.data)

        // Only queue speech if both audio and speech marks are present
        if (audio && speechMarks) {
          queueSpeech({
            id: new Date().getTime().toString(),
            base64: audio,
            speechMarks,
          })
        } else {
          console.log("[CHAT_CONTEXT] Sentence buffer received without audio/speech marks")
        }

        return
      }

      if (messageObj.isAudio) {
        audioChunk.current = {
          id: new Date().getTime().toString(),
          base64: messageObj.audioBase64,
          speechMarks: [],
        }
        return
      }

      // Speech Marks Messages:
      if (messageObj.isSpeechMarks) {
        // Done
        if (messageObj.isSpeechMarksDone) {
          console.log(
            "[CHAT_CONTEXT] Speechmarks done message",
            JSON.stringify(messageObj, null, 2),
          )
          // Signal that all audio chunks have been received from backend
          signalAllAudioReceived()
          return
        }

        // Process the speech marks:
        // This is a speech marks message. Attach the speech marks to the most recent audio chunk.
        // we're assuming that audio will always arrive first, then speech marks
        if (audioChunk.current) {
          if (audioChunk.current.processed) {
            console.warn(
              "[CHAT_CONTEXT] Audio chunk already processed, skipping duplicate speech marks message.",
            )
            return
          }

          if (audioChunk.current.speechMarks.length === 0) {
            audioChunk.current.speechMarks = messageObj.data
            audioChunk.current.processed = true
            queueSpeech({ ...audioChunk.current }) // Spread it so it creates a new object.
            audioChunk.current = undefined
          } else {
            console.error(
              "[CHAT_CONTEXT] Speech marks already attached to audio chunk: ",
              audioChunk.current,
            )
          }
        } else {
          console.error("[CHAT_CONTEXT] No audio chunk to attach speech marks to")
        }
        return
      }

      // Event Messages:
      if (messageObj.isEvent) {
        console.log("[CHAT_CONTEXT] Catchall event message", messageObj)

        // Handle start_conversation event (both client-initiated and server-initiated sidekick switches)
        if (messageObj.isStartConversation()) {
          const isServerInitiated = !startConversationStatus?.isPending

          if (!messageObj.parsedData) {
            console.error("[CHAT_CONTEXT]: start_conversation event is missing parsed data")
            return
          }

          const message = messageObj.parsedData
          const args = message.args

          if (isServerInitiated) {
            console.log(
              "[CHAT_CONTEXT] Received server-initiated start_conversation event (sidekick switch)",
            )
            posthog?.capture("switch_sidekick", {
              taskId: args.task_id ?? null,
              activeESKSponsor: args.esk_sponsor,
              activeESKType: args.esk_type,
              sidekickAvatarId: args.sidekick_avatar_id,
              sidekickName: args.sidekick_name,
              skPlatform: "app",
            })
          } else {
            console.log("[CHAT_CONTEXT] Received start_conversation event, updating conversation")
          }

          // Update the conversation with the details from the start_conversation event
          updateConversationFromStartMessage(message)
          queryClient.setQueryData(["startConversationStatus"], { isPending: false })

          // Reset switching state since the switch is now complete.
          // Always call unconditionally — `setState(false)` is idempotent, and
          // gating on the captured `isSwitchingSidekick` value drops the reset
          // when a SwitchCM event (which sets it true) and the start_conversation
          // event arrive in the same handler tick: the closure here still sees
          // the pre-update value (false), so the "Transferring..." indicator
          // would remain stuck.
          setIsSwitchingSidekick(false)

          // Start waiting for welcome message after the conversation has started
          startWaitingForWelcomeMessage()

          // If a server-initiated sidekick switch lands on a non-media CM while the
          // user is inside a media player, close the player and return home.
          // Only applies to server-initiated switches — when the user manually selects
          // a CM from the content library (client-initiated), they may have already
          // pushed the player onto the stack themselves and closing it here would
          // cause a spurious chat flash followed by a redirect back to the library.
          if (isServerInitiated && !args.media_mode && MEDIA_PLAYER_ROUTES.has(pathname)) {
            router.navigate("/")
          }
        }
        setMessageEvents((prev) => [...prev, messageObj])
      }
    },
    [
      currentConversation,
      startConversationStatus?.isPending,
      queryClient,
      api,
      resetTypingString,
      setAwaitingUserSentence,
      setAwaitingESKResponse,
      cancelTimingOnError,
      cancelWaitingForWelcomeMessage,
      verifyAndRestoreSession,
      startNewConversation,
      streamingReady,
      getTypingMessageValue,
      appendMessageToHistory,
      resolveWaitingForWelcomeMessage,
      appendTypingString,
      posthog,
      queueSpeech,
      signalAllAudioReceived,
      startWaitingForWelcomeMessage,
      router,
      pathname,
    ],
  )

  // Ref to hold the latest handleMessage callback for use in queue processing
  const handleMessageRef = React.useRef(handleMessage)
  React.useEffect(() => {
    handleMessageRef.current = handleMessage
  }, [handleMessage])

  // onMessage is the websocket message receiver - it checks busy state and either queues or handles
  const onMessage = React.useCallback(
    async (e: MessageEvent<any>) => {
      const tempMsgObj = new WebSocketMessage(e.data)

      // If we're in busy queue mode, queue all inbound messages except errors
      if (isSidekickBusy) {
        // Don't queue error messages - they should be processed immediately
        if (!tempMsgObj.isError()) {
          console.log("[CHAT_CONTEXT] Sidekick busy, queueing message")
          busyQueueRef.current.push(e)
          return
        }
      }

      // Check if this is a start_conversation event that represents a sidekick switch while busy
      if (tempMsgObj.isStartConversation() && tempMsgObj.parsedData) {
        const args = tempMsgObj.parsedData.args
        const isSidekickSwitch =
          currentConversation &&
          currentConversation.sidekick_avatar_id !== parseInt(args.sidekick_avatar_id)

        // Queue the switch (and all subsequent messages) so that in-progress audio from the
        // outgoing sidekick finishes before we update the conversation and play the new
        // sidekick's audio. This applies to both client-initiated and server-initiated transfers.
        if (isSidekickSwitch && getIsSidekickCurrentlyBusy()) {
          console.log("[CHAT_CONTEXT] Sidekick switch detected while busy, enabling busy queue", {
            currentAvatar: currentConversation?.sidekick_avatar_id,
            newAvatar: args.sidekick_avatar_id,
          })
          setIsSidekickBusy(true)
          busyQueueRef.current.push(e)
          return
        }
      }

      // Process the message
      await handleMessage(e)
    },
    [isSidekickBusy, handleMessage, currentConversation, getIsSidekickCurrentlyBusy],
  )

  // Ref to hold the latest onMessage callback - this allows the event listener
  // to always call the most recent version without needing to be re-registered
  // FIXME: this mechanism is needed to support websocket message handling on the web target.
  // It's likely that a different websocket library wouldn't have this issue on web, or perhaps
  // they will fix their issues with re-registration in the future.
  const onMessageRef = React.useRef(onMessage)
  React.useEffect(() => {
    onMessageRef.current = onMessage
  }, [onMessage])

  // Handle state/flags cleanup on connection state changes
  React.useEffect(() => {
    if (isConnected) {
      // Reset the user interaction state on a new connection
      setAwaitingUserSentence(false)
      setAwaitingESKResponse(false)
      setIsSwitchingSidekick(false)
      wordsDone.current = true
    } else {
      // If connection is lost while waiting for welcome message, cancel the wait.
      // Cleanup is idempotent, so call unconditionally.
      cancelWaitingForWelcomeMessage()
    }
  }, [isConnected, setAwaitingESKResponse, setAwaitingUserSentence, cancelWaitingForWelcomeMessage])

  React.useEffect(() => {
    if (isConnected && reconnectionMessages.length > 0) {
      console.log("[CHAT_CONTEXT] Sending startup messages", reconnectionMessages)
      reconnectionMessages.forEach(({ socketMessage, messageData }) => {
        wsSendMessage(socketMessage)
        if (messageData) {
          appendMessageToHistory(messageData)
        }
      })
      setReconnectionMessages([])
    }
  }, [appendMessageToHistory, isConnected, reconnectionMessages, wsSendMessage])

  // Define our message event listener from the websocket.
  // Uses onMessageRef to always call the latest onMessage without re-registering the listener
  React.useEffect(() => {
    let webSocketLike: WebSocketLike | null = null

    // Wrapper that always calls the latest onMessage via ref
    const messageHandler = (e: Event) => {
      onMessageRef.current(e as MessageEvent<any>).catch((error) => {
        console.error("[CHAT_CONTEXT] Error in onMessage:", error)
      })
    }

    if (isConnected) {
      webSocketLike = getWebSocket()
      if (webSocketLike && "onmessage" in webSocketLike) {
        webSocketLike.addEventListener("message", messageHandler)
      }
    }
    return () => {
      if (webSocketLike) {
        webSocketLike.removeEventListener("message", messageHandler)
      }
    }
  }, [getWebSocket, isConnected])

  const sendMessage = React.useCallback(
    async (message: string) => {
      // Stop playback before setting awaitingESKResponse so that when we set it, isSpeaking is
      // already false. Otherwise the "clear when isSpeaking" effect would immediately clear the
      // flag we set (interrupt-with-typed-message case).
      await stopSpeech()
      const mediaContextInfo = getMediaContextInfo()
      const currentLocation = await refreshLocation()
      console.log("[CHAT_CONTEXT] sendMessage location:", currentLocation)
      const socketMessage: Record<string, unknown> = {
        text: message,
        isAudioOn: true,
        isDebugOn: false,
        ttsConfig: currentTTSEngine,
        type: WebSocketMessageType.MemberMessage,
        location: currentLocation ?? null,
      }
      // Include media context if user is viewing media
      if (mediaContextInfo) {
        socketMessage.media_context_info = mediaContextInfo
      }
      const messageId = new Date().getTime()
      const messageData: TMessage = {
        id: messageId,
        message_id: `member-${messageId}`,
        text: message,
        role: "member",
        created_at: nowInUTC(),
      }
      console.log("[CHAT_CONTEXT] Sending message with audio response", socketMessage)
      setAwaitingESKResponse(true)
      // Words are NOT done when sending a new member message
      wordsDone.current = false
      sendMessageWithTokenVerification(socketMessage, messageData)
    },
    [
      stopSpeech,
      getMediaContextInfo,
      refreshLocation,
      currentTTSEngine,
      setAwaitingESKResponse,
      sendMessageWithTokenVerification,
    ],
  )

  const sendSentimentMessage = React.useCallback(
    (option: string, sentimentString?: string) => {
      const socketMessage: TAddSentiment = {
        sentiment_value: option,
        isAudioOn: AUDIO_ON,
        isDebugOn: false,
        type: WebSocketMessageType.UserWellnessStateUpdate,
      }
      console.log("[CHAT_CONTEXT] Sending sentiment message", socketMessage)
      sendMessageWithTokenVerification(socketMessage)
      if (sentimentString) {
        const text = `I currently feel ${sentimentString}`
        sendMessage(text)
      }
    },
    [sendMessage, sendMessageWithTokenVerification],
  )

  // Watch the sentiment value and send a message to the websocket when it changes.
  React.useEffect(() => {
    if (pendingSentiment) {
      sendSentimentMessage(pendingSentiment, sentimentText)
      commitSentiment()
    }
  }, [commitSentiment, pendingSentiment, sendSentimentMessage, sentimentText])

  // Sends a message over websockets that the user wants to report something.
  const sendUserReport = (message: string) => {
    // TODO: Add some unique identifiers here to the data.
    const reportMessage = {
      text: message,
      type: "report",
    }
    console.log("[CHAT_CONTEXT] Sending report message", reportMessage)
    sendMessageWithTokenVerification(reportMessage)
  }

  const { onChatMessageWithEvents } = useChatMessageEvents()

  // Process message events set by the websocket connection.
  React.useEffect(() => {
    if (messageEvents.length > 0 && wordsDone.current) {
      for (const message of messageEvents) {
        onChatMessageWithEvents(message)
      }
      setMessageEvents([])
    }
  }, [messageEvents, onChatMessageWithEvents])

  // Process the busy queue. Waits for any in-progress audio to finish first, then
  // processes all queued messages without additional per-message waits. Audio ordering
  // for the new sidekick's messages is handled by the speech queue itself, so gating
  // each message on audio completion would delay text appearing in the chat.
  const processBusyQueue = React.useCallback(async () => {
    try {
      // Let any currently-playing audio (e.g. the transfer confirmation) finish before
      // updating the conversation and processing the new sidekick's messages.
      await waitForSpeechQueueEmpty()
      console.log("[CHAT_CONTEXT] Speech queue empty, processing busy queue")

      while (busyQueueRef.current.length > 0) {
        const nextMessage = busyQueueRef.current.shift()
        if (nextMessage) {
          console.log(
            "[CHAT_CONTEXT] Processing queued message, remaining:",
            busyQueueRef.current.length,
          )
          await handleMessageRef.current(nextMessage)
        }
      }

      console.log("[CHAT_CONTEXT] Busy queue fully drained")
    } finally {
      setIsSidekickBusy(false)
    }
  }, [waitForSpeechQueueEmpty])

  // Track whether we're currently processing the queue to prevent concurrent processing
  const isProcessingQueueRef = React.useRef(false)

  // Trigger queue processing when busy mode is enabled
  React.useEffect(() => {
    if (isSidekickBusy && !isProcessingQueueRef.current && busyQueueRef.current.length > 0) {
      isProcessingQueueRef.current = true
      processBusyQueue()
        .catch((error) => {
          console.error("[CHAT_CONTEXT] Error processing busy queue:", error)
          setIsSidekickBusy(false)
        })
        .finally(() => {
          isProcessingQueueRef.current = false
        })
    }
  }, [isSidekickBusy, processBusyQueue])

  const startMicStream = React.useCallback(() => {
    const messageData: WebsocketAudioStreamingStartMessageData = {
      isAudioOn: true,
      isDebugOn: false,
      sttId: "aws_transcribe",
      type: "start streaming",
      isSilenceActive: false, // this property turns on and off "walkie-talkie mode"
      sample_rate: SAMPLE_RATE_BASE,
    }
    console.log("[CHAT_CONTEXT] Sending start streaming message", messageData)
    console.log("[CHAT_CONTEXT] Websocket connected:", isConnected)
    sendMessageWithTokenVerification(messageData)
  }, [sendMessageWithTokenVerification, isConnected])

  const appState = useAppState()
  const sendAudioChunk = React.useCallback(
    (chunk: Buffer) => {
      // Don't send audio when app is in background/inactive so we don't keep sending on a dying socket
      if (appState !== "active") {
        return
      }
      // Send the audio chunk:
      console.log("[CHAT_CONTEXT] Sending audio chunk, size:", chunk.length, "bytes")

      const data = (function () {
        if (Platform.OS === "web") {
          return chunk
        } else {
          return chunk.buffer.slice(chunk.byteOffset, chunk.byteOffset + chunk.byteLength)
        }
      })()
      console.log(
        "[CHAT_CONTEXT] Prepared data for websocket, size:",
        data.byteLength || (data as any).length,
        "bytes",
      )
      wsSendBinaryMessage(data)
    },
    [appState, wsSendBinaryMessage],
  )

  const stopMicStream = React.useCallback(() => {
    const mediaContextInfo = getMediaContextInfo()
    const messageData: WebsocketAudioStreamingEndMessageData = {
      isAudioOn: true,
      isDebugOn: false,
      type: "end streaming",
      media_context_info: mediaContextInfo,
    }
    console.log("[CHAT_CONTEXT] Sending end streaming message", messageData)
    wordsDone.current = false
    wsSendMessage(messageData)
  }, [wsSendMessage, getMediaContextInfo])

  const cancelMicStream = React.useCallback(() => {
    const messageData = {
      type: WebSocketMessageType.CancelStreaming,
    }
    console.log("[CHAT_CONTEXT] Sending cancel streaming message", messageData)
    setAwaitingUserSentence(false)
    setAwaitingESKResponse(false)
    wordsDone.current = true
    wsSendMessage(messageData)
  }, [wsSendMessage, setAwaitingUserSentence, setAwaitingESKResponse])

  const sendSimulateEvent = React.useCallback(
    (eventType: string) => {
      const messageData = {
        event: eventType,
        type: "simulate_event",
      }
      console.log("[CHAT_CONTEXT] Sending simulate event message", messageData)
      sendMessageWithTokenVerification(messageData)
    },
    [sendMessageWithTokenVerification],
  )

  // Validate that the persisted LLM ID is still available, clear if not
  useEffect(() => {
    if (llmAvailable.data && currentLLMId) {
      const foundLLM = llmAvailable.data.find((llm) => llm.id === currentLLMId)
      if (!foundLLM) {
        console.warn(
          `[CHAT_CONTEXT] Persisted LLM ID "${currentLLMId}" is no longer available. Clearing selection to use backend default.`,
        )
        setCurrentLLMId(undefined)
      }
    }
  }, [llmAvailable.data, currentLLMId, setCurrentLLMId])

  // Sync the current LLM ID to ResponseTimingContext
  useEffect(() => {
    if (currentLLMId) {
      setResponseTimingLLMId(currentLLMId)
    }
  }, [currentLLMId, setResponseTimingLLMId])

  const value = {
    sendMessage,
    startConversation,
    startNewConversation,
    showDebugMessages,
    setShowDebugMessages,
    audioStreamingReady,
    startMicStream,
    sendAudioChunk,
    stopMicStream,
    cancelMicStream,
    cancelWaitingForWelcomeMessage,
    sendSimulateEvent,
    sendUserReport,
    isSwitchingSidekick,
    setIsSwitchingSidekick,
    currentLLMId, // Can be undefined when no LLM explicitly selected (backend will use default)
    setCurrentLLMId,
    startConversationStatus,
    startConversationTimedOut,
    clearStartConversationTimedOut,
    wordsDone: wordsDone.current,
    forceReconnect,
    waitingForWelcomeMessage,
    isSidekickBusy,
  }
  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>
}

export const useChat = () => {
  const context = React.useContext(ChatContext)
  if (!context) {
    throw new Error("useChat must be used within an ChatProvider")
  }
  return context
}
